import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";
import useGetUserRoleFlags from "../hooks/useGetUserRoleFlags";
import {
  ROLE_ADMIN_CHANGE_NUMBER_RANGES,
  ROLE_PORTAL_ADMIN,
} from "../constants/appRoles";
import AdminRoleInfoTooltip from "./AdminRoleInfoTooltip";
import AdminAgvChipCanvas, { type AgvCanvasChip } from "./AdminAgvChipCanvas";
import type {
  NumberRangeBaseFunction,
  NumberRangeDescription,
  NumberRangeSubFunction,
} from "../shared/NumberRange";
import { descriptionTooltipText } from "../shared/NumberRange";
import { AGV_OVERLAY_MAX_WIDTH_PX } from "../shared/numberRangeChipLayout";
import { invalidateNumberRangeBaseFunctionsCache } from "../shared/numberRangeBasesCache";

const FLAT_ACCORDION_SX = {
  border: "none",
  boxShadow: "none",
  "&::before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

type Props = {
  active: boolean;
  /** Bump when labels are saved so other views (e.g. number range usage) can refetch. */
  onLabelsSaved?: () => void;
};

function englishDisplayName(descriptions: NumberRangeDescription[] | undefined): string | null {
  const d = descriptions?.find((x) => x.language_code === "eng");
  const name = d?.display_name?.trim();
  return name ? name : null;
}

function normalizeList<T>(data: unknown): T[] {
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as { results?: unknown }).results)
  ) {
    return (data as { results: T[] }).results;
  }
  return [];
}

const DescriptionRow: React.FC<{
  desc: NumberRangeDescription;
  disabled: boolean;
  onAfterSave: () => void;
}> = ({ desc, disabled, onAfterSave }) => {
  const { callApi } = useEventsApi();
  const [displayName, setDisplayName] = useState(desc.display_name ?? "");
  const [description, setDescription] = useState(desc.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(desc.display_name ?? "");
    setDescription(desc.description ?? "");
    setError(null);
  }, [desc.id, desc.display_name, desc.description]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const base = config.apiUrl.replace(/\/+$/, "");
      await callApi(`${base}/api/number-range-description/${desc.id}/`, "PATCH", {
        display_name: displayName.trim() || null,
        description: description.trim() || null,
      });
      await onAfterSave();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "responseBody" in err
          ? String((err as { responseBody?: string }).responseBody)
          : String(err);
      const forbidden =
        (err && typeof err === "object" && "status" in err && (err as { status?: number }).status === 403) ||
        msg.includes("403");
      setError(
        forbidden
          ? "Save denied — assign the required number-range API app role in Entra, then sign out and in (see the info icon above for role names)."
          : msg.slice(0, 220)
      );
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    displayName !== (desc.display_name ?? "") || description !== (desc.description ?? "");

  return (
    <TableRow>
      <TableCell sx={{ verticalAlign: "top", width: 100 }}>
        <Chip size="small" label={desc.language_code} variant="outlined" />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <TextField
          size="small"
          fullWidth
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={disabled || saving}
          inputProps={{ "aria-label": `Display name ${desc.language_code}` }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={disabled || saving}
          inputProps={{ "aria-label": `Description ${desc.language_code}` }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top", width: 120 }}>
        <Button
          variant="outlined"
          size="small"
          disabled={disabled || saving || !dirty}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        {error && (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const AdminNumberRangeLabelsPanel: React.FC<Props> = ({ active, onLabelsSaved }) => {
  const { callApi } = useEventsApi();
  const callApiRef = useRef(callApi);
  callApiRef.current = callApi;
  const loadRef = useRef<((opts?: { silent?: boolean }) => Promise<void>) | null>(null);

  const { isAdmin } = useGetUserRoleFlags();
  const [bases, setBases] = useState<NumberRangeBaseFunction[]>([]);
  /** Only bases present in this map have had subs fetched (value may be an empty array). */
  const [subsByBaseId, setSubsByBaseId] = useState<Record<number, NumberRangeSubFunction[]>>({});
  const subsByBaseIdRef = useRef(subsByBaseId);
  subsByBaseIdRef.current = subsByBaseId;

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [baseAccordionOpen, setBaseAccordionOpen] = useState<Record<number, boolean>>({});
  const [subsLoadingIds, setSubsLoadingIds] = useState<Record<number, boolean>>({});
  const [subsLoadError, setSubsLoadError] = useState<Record<number, string>>({});
  const [newBaseDigit, setNewBaseDigit] = useState("");
  const [createBaseBusy, setCreateBaseBusy] = useState(false);
  const [createBaseError, setCreateBaseError] = useState<string | null>(null);
  const [baseLayoutEdit, setBaseLayoutEdit] = useState(false);
  const [showOnAgvBusyId, setShowOnAgvBusyId] = useState<number | null>(null);
  const patchTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    return () => {
      Object.values(patchTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  const fetchSubsForBase = useCallback(async (baseId: number, opts?: { force?: boolean }) => {
    if (!opts?.force && baseId in subsByBaseIdRef.current) {
      return;
    }
    setSubsLoadingIds((prev) => ({ ...prev, [baseId]: true }));
    setSubsLoadError((prev) => {
      const next = { ...prev };
      delete next[baseId];
      return next;
    });
    try {
      const baseUrl = config.apiUrl.replace(/\/+$/, "");
      const subRes = await callApiRef.current(
        `${baseUrl}/api/number-range-sub-function/?base=${baseId}&page_size=all`,
        "GET"
      );
      if (subRes === undefined) {
        setSubsLoadError((prev) => ({
          ...prev,
          [baseId]: "Not signed in or token unavailable.",
        }));
        return;
      }
      const subs = normalizeList<NumberRangeSubFunction>(subRes);
      setSubsByBaseId((prev) => ({
        ...prev,
        [baseId]: [...subs].sort((s, t) => s.digit - t.digit),
      }));
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "responseBody" in err
          ? String((err as { responseBody?: string }).responseBody)
          : String(err);
      setSubsLoadError((prev) => ({
        ...prev,
        [baseId]: msg.slice(0, 220),
      }));
    } finally {
      setSubsLoadingIds((prev) => {
        const next = { ...prev };
        delete next[baseId];
        return next;
      });
    }
  }, []);

  /** Stable callback: MSAL may return a new `callApi` reference each render; a ref avoids reload loops. */
  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setLoading(true);
      setLoadError(null);
    }
    try {
      const baseUrl = config.apiUrl.replace(/\/+$/, "");
      const basesRes = await callApiRef.current(`${baseUrl}/api/number-range-base-function/?page_size=all`, "GET");
      if (basesRes === undefined) {
        if (!silent) {
          setLoadError("Not signed in or token unavailable.");
        }
        setBases([]);
        setSubsByBaseId({});
        return;
      }
      const baseList = normalizeList<NumberRangeBaseFunction>(basesRes);
      if (
        !Array.isArray(basesRes) &&
        !(basesRes && typeof basesRes === "object" && Array.isArray((basesRes as { results?: unknown }).results))
      ) {
        if (!silent) {
          setLoadError("Unexpected response when loading number range base functions.");
        }
        setBases([]);
        setSubsByBaseId({});
        return;
      }
      setBases([...baseList].sort((a, b) => a.digit - b.digit));

      if (!silent) {
        setSubsByBaseId({});
      } else {
        const prevSubs = subsByBaseIdRef.current;
        const baseIdsToRefresh = Object.keys(prevSubs).map(Number);
        if (baseIdsToRefresh.length > 0) {
          const pairs = await Promise.all(
            baseIdsToRefresh.map(async (baseId) => {
              try {
                const subRes = await callApiRef.current(
                  `${baseUrl}/api/number-range-sub-function/?base=${baseId}&page_size=all`,
                  "GET"
                );
                const subs = normalizeList<NumberRangeSubFunction>(subRes ?? []);
                return {
                  baseId,
                  subs: [...subs].sort((s, t) => s.digit - t.digit),
                };
              } catch {
                return { baseId, subs: prevSubs[baseId] ?? [] };
              }
            })
          );
          setSubsByBaseId((prev) => {
            const next = { ...prev };
            for (const { baseId, subs } of pairs) {
              if (baseId in prev) {
                next[baseId] = subs;
              }
            }
            return next;
          });
        }
      }
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status?: number }).status
          : undefined;
      const body =
        err && typeof err === "object" && "responseBody" in err
          ? String((err as { responseBody?: string }).responseBody).slice(0, 300)
          : "";
      if (!silent) {
        setLoadError(
          `Failed to load number ranges${status ? ` (HTTP ${status})` : ""}.${body ? ` ${body}` : " Check network and API access."}`
        );
      }
      if (!silent) {
        setBases([]);
        setSubsByBaseId({});
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);
  loadRef.current = load;

  const queuePatchBase = useCallback(
    (id: number, chip_top: number, chip_left: number) => {
      const k = `base-${id}`;
      if (patchTimersRef.current[k]) clearTimeout(patchTimersRef.current[k]);
      const baseUrl = config.apiUrl.replace(/\/+$/, "");
      patchTimersRef.current[k] = setTimeout(() => {
        void (async () => {
          try {
            await callApiRef.current(`${baseUrl}/api/number-range-base-function/${id}/`, "PATCH", {
              chip_top,
              chip_left,
            });
            invalidateNumberRangeBaseFunctionsCache();
            await loadRef.current?.({ silent: true });
            onLabelsSaved?.();
          } catch {
            /* optional: surface toast */
          } finally {
            delete patchTimersRef.current[k];
          }
        })();
      }, 450);
    },
    [onLabelsSaved]
  );

  const reloadAfterSave = useCallback(async () => {
    invalidateNumberRangeBaseFunctionsCache();
    await loadRef.current?.({ silent: true });
    onLabelsSaved?.();
  }, [onLabelsSaved]);

  useEffect(() => {
    if (active) {
      void load();
    }
  }, [active, load]);

  const renderDescriptionTable = (descriptions: NumberRangeDescription[] | undefined, keyPrefix: string) => {
    const list = [...(descriptions ?? [])].sort((a, b) => a.language_code.localeCompare(b.language_code));
    if (list.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No descriptions for this row.
        </Typography>
      );
    }
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Language</TableCell>
            <TableCell>Display name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right"> </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((d) => (
            <DescriptionRow
              key={`${keyPrefix}-${d.id}`}
              desc={d}
              disabled={!isAdmin}
              onAfterSave={reloadAfterSave}
            />
          ))}
        </TableBody>
      </Table>
    );
  };

  const createBaseFunction = async () => {
    const parsed = Number(newBaseDigit);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 9) {
      setCreateBaseError("Digit must be an integer between 0 and 9.");
      return;
    }
    setCreateBaseBusy(true);
    setCreateBaseError(null);
    try {
      const baseUrl = config.apiUrl.replace(/\/+$/, "");
      await callApiRef.current(`${baseUrl}/api/number-range-base-function/`, "POST", {
        digit: parsed,
      });
      setNewBaseDigit("");
      invalidateNumberRangeBaseFunctionsCache();
      await loadRef.current?.({ silent: true });
      onLabelsSaved?.();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "responseBody" in err
          ? String((err as { responseBody?: string }).responseBody)
          : String(err);
      setCreateBaseError(msg.slice(0, 240));
    } finally {
      setCreateBaseBusy(false);
    }
  };

  const usedDigits = new Set(bases.map((b) => b.digit));
  const availableDigits = Array.from({ length: 10 }, (_, i) => i).filter((d) => !usedDigits.has(d));

  const patchBaseShowOnAgv = async (bf: NumberRangeBaseFunction, show_on_agv: boolean) => {
    setShowOnAgvBusyId(bf.id);
    try {
      const baseUrl = config.apiUrl.replace(/\/+$/, "");
      await callApiRef.current(`${baseUrl}/api/number-range-base-function/${bf.id}/`, "PATCH", {
        show_on_agv,
      });
      invalidateNumberRangeBaseFunctionsCache();
      await loadRef.current?.({ silent: true });
      onLabelsSaved?.();
    } catch {
      /* optional toast */
    } finally {
      setShowOnAgvBusyId(null);
    }
  };

  const baseAgvChips: AgvCanvasChip[] = useMemo(
    () =>
      bases
        .filter((bf) => bf.show_on_agv !== false)
        .sort((a, b) => a.digit - b.digit || a.id - b.id)
        .map((bf) => ({
          id: bf.id,
          kind: "base" as const,
          digit: bf.digit,
          label: englishDisplayName(bf.descriptions) ?? `Base ${bf.digit}`,
          chip_top: bf.chip_top,
          chip_left: bf.chip_left,
          tooltip: descriptionTooltipText(bf.descriptions, "eng"),
        })),
    [bases]
  );

  return (
    <Box sx={{ px: 1.5, py: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" component="span">
          Edit display names, descriptions, and AGV chip layout for each number-range base and sub (per language).
        </Typography>
        <AdminRoleInfoTooltip ariaLabel="Number range label permissions in Entra">
          <>
            This screen is available with portal role <code>{ROLE_PORTAL_ADMIN}</code>. Saving calls PATCH on{" "}
            <code>/api/number-range-description/&lt;id&gt;/</code>, which requires the Event Manager{" "}
            <strong>API application role</strong> <code>{ROLE_ADMIN_CHANGE_NUMBER_RANGES}</code> in Microsoft
            Entra (or <code>REQUIRED_ROLE_CHANGE_NUMBER_RANGES</code> on the server). Chip positions use PATCH on{" "}
            <code>/api/number-range-base-function/&lt;id&gt;/</code> for base chip layout (<code>chip_top</code>,{" "}
            <code>chip_left</code> as 0–100 percent, and <code>show_on_agv</code>). Sub-functions are edited as text
            only (no AGV preview).
          </>
        </AdminRoleInfoTooltip>
      </Stack>
      <Stack direction="column" spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
        <Box sx={{ width: "100%", maxWidth: AGV_OVERLAY_MAX_WIDTH_PX, mx: "auto", flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }} flexWrap="wrap">
            <Tooltip
              title={
                loading
                  ? "Loading number range bases — layout edit is disabled until chips are up to date."
                  : baseLayoutEdit
                    ? "Exit layout edit"
                    : "Edit base chip positions"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color={baseLayoutEdit ? "primary" : "default"}
                  disabled={!isAdmin || bases.length === 0 || loading}
                  onClick={() => setBaseLayoutEdit((v) => !v)}
                  aria-label="Toggle base chip layout edit"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {loading && (
              <Chip
                size="small"
                variant="outlined"
                color="default"
                icon={
                  <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                }
                label="Loading base chips…"
                sx={{ maxWidth: "100%" }}
              />
            )}
            {baseLayoutEdit && !loading && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                Drag chips; positions save shortly after release.
              </Typography>
            )}
          </Stack>
          <Box sx={{ position: "relative", width: "100%", maxWidth: AGV_OVERLAY_MAX_WIDTH_PX, mx: "auto" }}>
            {loading && (
              <Box
                aria-busy="true"
                aria-live="polite"
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  borderRadius: 1,
                  bgcolor: (t) => alpha(t.palette.background.paper, 0.78),
                  backdropFilter: "blur(3px)",
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="caption" color="text.secondary">
                  Loading AGV preview chips…
                </Typography>
              </Box>
            )}
            <AdminAgvChipCanvas
              chips={baseAgvChips}
              layoutEditMode={baseLayoutEdit && isAdmin && !loading}
              onCommitPercent={(kind, id, top, left) => {
                if (kind === "base") queuePatchBase(id, top, left);
              }}
            />
          </Box>
        </Box>
        <Paper
          variant="outlined"
          sx={{ p: 1.5, width: "100%", maxWidth: AGV_OVERLAY_MAX_WIDTH_PX, mx: "auto", minWidth: 0 }}
        >
          <Typography variant="subtitle2">Create base function</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Allowed base digits: 0-9. Maximum: 10 base functions. New bases appear at the center until you move them.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
            <Select
              size="small"
              value={newBaseDigit}
              onChange={(e) => setNewBaseDigit(String(e.target.value))}
              displayEmpty
              disabled={createBaseBusy || !isAdmin || availableDigits.length === 0 || loading}
              sx={{ width: 180 }}
            >
              <MenuItem value="">
                <em>Select digit</em>
              </MenuItem>
              {availableDigits.map((digit) => (
                <MenuItem key={digit} value={String(digit)}>
                  {digit}
                </MenuItem>
              ))}
            </Select>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => void createBaseFunction()}
              disabled={
                createBaseBusy || !isAdmin || newBaseDigit === "" || availableDigits.length === 0 || loading
              }
            >
              {createBaseBusy ? "Creating…" : "Create base"}
            </Button>
          </Stack>
          {availableDigits.length === 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              All base digits (0-9) are already in use.
            </Typography>
          )}
          {createBaseError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.75 }}>
              {createBaseError}
            </Typography>
          )}
        </Paper>
      </Stack>
      {loadError && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {bases.map((bf) => {
            const subsLoaded = bf.id in subsByBaseId;
            const subCount = subsLoaded ? subsByBaseId[bf.id].length : null;
            const engLabel = englishDisplayName(bf.descriptions);
            return (
              <Paper key={bf.id} elevation={0} sx={{ overflow: "hidden", border: "none", boxShadow: "none" }}>
                <Accordion
                  expanded={Boolean(baseAccordionOpen[bf.id])}
                  onChange={(_, exp) => {
                    setBaseAccordionOpen((prev) => ({ ...prev, [bf.id]: exp }));
                    if (exp) {
                      void fetchSubsForBase(bf.id);
                    }
                  }}
                  disableGutters
                  sx={FLAT_ACCORDION_SX}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`number-range-base-${bf.id}-content`}
                    id={`number-range-base-${bf.id}-header`}
                  >
                    <Box sx={{ pr: 1, minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={600} noWrap title={engLabel ?? undefined}>
                        Base digit {bf.digit}
                        {engLabel ? ` — ${engLabel}` : ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        id {bf.id}
                        {subCount !== null
                          ? ` · ${subCount} sub-function${subCount === 1 ? "" : "s"}`
                          : ""}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                    <Divider />
                    <Box sx={{ px: 2, py: 2 }}>
                      <FormControlLabel
                        sx={{ mb: 1.5, ml: 0, alignItems: "flex-start" }}
                        control={
                          <Switch
                            size="small"
                            checked={bf.show_on_agv !== false}
                            disabled={!isAdmin || showOnAgvBusyId === bf.id}
                            onChange={(_, v) => void patchBaseShowOnAgv(bf, v)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" component="span" display="block">
                              Show as chip on AGV image
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              When off, this base is hidden in the event creator and in the preview above.
                            </Typography>
                          </Box>
                        }
                      />
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Base labels
                      </Typography>
                      {renderDescriptionTable(bf.descriptions, `base-${bf.id}`)}
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Sub-functions
                      </Typography>
                      {subsLoadingIds[bf.id] ? (
                        <Box display="flex" alignItems="center" gap={1} py={1}>
                          <CircularProgress size={22} />
                          <Typography variant="body2" color="text.secondary">
                            Loading sub-functions…
                          </Typography>
                        </Box>
                      ) : subsLoadError[bf.id] ? (
                        <Box>
                          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                            {subsLoadError[bf.id]}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => void fetchSubsForBase(bf.id, { force: true })}
                          >
                            Retry
                          </Button>
                        </Box>
                      ) : !subsLoaded ? (
                        <Typography variant="body2" color="text.secondary">
                          Open this section to load sub-functions.
                        </Typography>
                      ) : subsByBaseId[bf.id].length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No sub-functions for this base.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {subsByBaseId[bf.id].map((sf) => (
                            <Box key={sf.id}>
                              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                                Sub digit {sf.digit} (id {sf.id})
                              </Typography>
                              {renderDescriptionTable(sf.descriptions, `sub-${sf.id}`)}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            );
          })}
          {bases.length === 0 && !loadError && (
            <Typography variant="body2" color="text.secondary">
              No number range base functions returned.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default AdminNumberRangeLabelsPanel;
